/** ReferralInline - compact referral CTA for the Dashboard + post-purchase moment.
 * Full invite management lives at /account?tab=refer.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gift, Copy, MessageCircle, Mail, ArrowRight, Check } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Optional variant="compact" for post-purchase inline. */
const ReferralInline = ({ variant = "default", onDismiss }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/referrals/me`).then((r) => alive && setData(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data?.share_link) return null;
  const { share_link, reward_per_referral_days, referee_trial_days, stats } = data;

  const track = (action) => trackEvent("referral_engaged", {
    action, variant,
    invites_sent: stats?.invites_sent || 0,
    conversions: stats?.conversions || 0,
  });

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(share_link);
      setCopied(true);
      track("copy_link");
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Could not copy - long-press to copy manually"); }
  };
  const onWhatsApp = () => {
    track("share_whatsapp");
    const text = encodeURIComponent(
      `Hey - I'm prepping for the ADC Part 1 exam on PassADC. Use my link and get a ${referee_trial_days}-day free Pro trial: ${share_link}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const onEmail = () => {
    track("share_email");
    const subject = encodeURIComponent("Study with me for the ADC Part 1 exam");
    const body = encodeURIComponent(
      `Hi,\n\nI've been using PassADC to study for the ADC Part 1 - voice OSCE role-play, 1,300+ MCQs, on-demand tutor. Use my link for a ${referee_trial_days}-day free Pro trial:\n\n${share_link}\n\n`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const onManage = () => {
    track("open_manage");
    navigate("/account?tab=refer");
  };

  const totalRewarded = stats?.total_reward_days || 0;

  return (
    <div
      data-testid={`referral-inline-${variant}`}
      className={`relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent p-5 sm:p-6 ${variant === "compact" ? "" : "md:p-8"}`}
    >
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 text-xs text-muted-foreground hover:text-foreground"
          data-testid="referral-dismiss-btn"
        >
          Close
        </button>
      )}
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
          <Gift className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-bold leading-tight">
            Give {referee_trial_days} days, get {reward_per_referral_days} days.
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Invite an ADC candidate - they get a {referee_trial_days}-day free Pro trial. When they upgrade, <strong>you earn {reward_per_referral_days} free Pro days.</strong>
            {totalRewarded > 0 && <> You&apos;ve already earned <strong className="text-success">{totalRewarded} days</strong>.</>}
          </p>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              size="sm"
              onClick={onCopy}
              className="rounded-full text-xs"
              data-testid="referral-copy-btn"
            >
              {copied ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onWhatsApp}
              className="rounded-full text-xs"
              data-testid="referral-whatsapp-btn"
            >
              <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onEmail}
              className="rounded-full text-xs"
              data-testid="referral-email-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
            </Button>
            <button
              onClick={onManage}
              data-testid="referral-manage-link"
              className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 ml-auto self-center"
            >
              Manage invites <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralInline;
